import React, { useCallback, useEffect, useMemo, useState } from 'react'

import dynamic from 'next/dynamic'

import classNames from 'classnames'
import classnames from 'classnames'

import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import { Icon } from '~/components/atoms/Icon/Icon'
import { urlFor } from '~/components/atoms/Image/Image'
import Lozenge, { LozengeVariant } from '~/components/atoms/Lozenge/Lozenge'
import { SliderThemes } from '~/components/atoms/Slider/sliderThemes'
import SocialShare from '~/components/atoms/SocialShare/SocialShare'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'

import LozengeList from '../LozengeList/LozengeList'

import VideoArticlePreview from './VideoArticlePreview/VideoArticlePreview'

import styles from './VideoSlider.module.css'

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false })

const DynamicSlider = dynamic(
  () => import('~/components/atoms/DynamicSlider'),
  {
    ssr: false,
  }
)

export interface Video {
  _ref: string
  title: string
  videoUrl: string
  summary: string
  coverImage?: string
  watchTime?: string
  category: { name: string; slug: string; categoryOrder: number }[]
}

interface VideoSlide extends Video {
  slide: number | string
  setIsPlaying: (id: any) => void
  isPlaying: any
  theme?: ComponentTheme
}

export interface VideoSliderProps {
  flip: boolean
  theme?: ComponentTheme
  title: string
  videoSummary?: string
  testID?: string
  moduleId?: string
  video: VideoSlide[]
  filterableView?: boolean
}

const VideoSlide = ({
  title,
  videoUrl,
  summary,
  coverImage,
  slide,
  setIsPlaying,
  isPlaying,
  theme,
}: VideoSlide) => {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)
  const image = coverImage && urlFor(coverImage, 1000)

  return (
    <Grid>
      <Row className={styles.alignCenter}>
        {/* Slide Video */}
        <Col
          variant="col-7/12--md"
          cssClass={classNames(styles.videoContainer)}
        >
          <div className={styles.playerWrapper}>
            <ReactPlayer
              url={videoUrl}
              width="100%"
              height="100%"
              light={image || false}
              onPlay={() => {
                setIsPlaying(slide)
              }}
              playing={isPlaying === slide}
              muted={isPlaying !== slide}
              controls={true}
            />
          </div>
        </Col>

        {/* Slide Text */}
        <Col variant="col-5/12--md" cssClass={classNames(styles.slideContent)}>
          <div className={styles.text}>
            <span className={styles.playCircle}>
              <Icon
                type="PlayCircle"
                fill={theme === ComponentTheme.black ? 'white' : undefined}
              />
            </span>
            <Text
              size={TextSizes.primaryTitleLrg}
              Tag="h3"
              className={styles.slideTitle}
            >
              {title}
            </Text>
            <Text size={TextSizes.bodyMed} Tag="p" className={styles.slideBody}>
              {summary}
            </Text>

            <div className={styles.sliderShare}>
              <SocialShare
                articleTitle={''}
                showSocials={false}
                urlToShare={videoUrl}
                dialogTop={
                  title.split(' ').length + summary?.split(' ').length > 20 ||
                  isMobile
                }
                theme={theme}
              />

              <Button
                variant={
                  theme === ComponentTheme.black
                    ? ButtonVariants.secondaryLight
                    : ButtonVariants.secondaryDark
                }
                iconLeft={
                  <Icon
                    type="ShareYouTube"
                    fill={theme === ComponentTheme.black ? 'white' : undefined}
                  />
                }
                className={classnames(styles.button)}
                link={videoUrl}
                target="_blank"
              >
                Visit YouTube
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </Grid>
  )
}

export default function VideoSlider({
  theme,
  title = '<em>My</em>ArtBroker Talks',
  testID,
  moduleId,
  video,
  filterableView = false,
}: VideoSliderProps) {
  const ref = React.useRef<any>(null)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [isPlaying, setIsPlaying] = useState(null)
  const [currentActiveVideo, setCurrentActiveVideo] = useState(video[0]._ref)
  const [filterChanged, setFilterChanged] = useState(0)
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const [currentFilter, setCurrentFilter] = useState<{
    name: string
    slug: string
  } | null>(null)
  const [videoList, setVideoList] = useState(video)

  useEffect(() => {
    if (currentFilter === null) {
      setVideoList(video)
    } else {
      setVideoList(
        video.filter(item =>
          item.category?.some(cat => cat.name === currentFilter?.name)
        )
      )
    }
  }, [filterChanged, currentFilter])

  const renderInitialView = () => {
    return (
      <DynamicSlider
        slidesPerView={1}
        overflowVisible={false}
        spaceBetween={0}
        spacer={false}
        title=""
        onChange={() => {
          setHasInteracted(true)
        }}
        wrapperClass={styles.sliderWrapperClass}
        hideArrowsMobile
        customControls={styles.customSliderControls}
        useScrollbar
        onSlideChange={() => {
          setIsPlaying(null)
        }}
        theme={theme === ComponentTheme.black ? SliderThemes.dark : undefined}
      >
        {video &&
          video.map((slide, key) => (
            <VideoSlide
              {...slide}
              key={`video-slide-${key}`}
              slide={key}
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
              theme={theme}
            />
          ))}
      </DynamicSlider>
    )
  }

  const categoryList = useMemo(() => {
    const categories: {
      name: string
      slug: string
      order: number | null
    }[] = []

    video?.forEach(item => {
      const category = item.category

      category?.forEach(cat => {
        categories.push({
          name: cat.name,
          slug: cat.slug,
          order: cat.categoryOrder,
        })
      })
    })

    const uniqueCategories = categories.filter(
      (item, index, self) =>
        self.findIndex(obj => obj.name === item.name) === index
    )

    // sort categories by their category order properrty
    uniqueCategories.sort((a, b) => {
      if (a.order === null) return 1
      if (b.order === null) return -1
      return a.order - b.order
    })

    return uniqueCategories
  }, [video])

  const slideVideoSelector = useCallback(
    (slide: VideoSlide) => {
      setCurrentActiveVideo(slide._ref)
      setIsPlaying(slide._ref as any)
      // scroll to ref
      if (ref?.current) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
        })
      }
    },
    [video]
  )

  const renderFilterableView = useCallback(() => {
    const initialSlide = video.find(item => item._ref === currentActiveVideo)
    if (!initialSlide) return null

    return (
      <>
        <VideoSlide
          {...initialSlide}
          slide={currentActiveVideo}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          theme={theme}
        />
        {categoryList.length > 0 && (
          <div className={styles.filterContainer}>
            <LozengeList className={classNames(styles.filtersLozengeList)}>
              {categoryList.map(
                (category: { name: string; slug: string }, i: number) => (
                  <Lozenge
                    title={category.name}
                    key={`category-lozenge-${i}`}
                    variant={LozengeVariant.gray}
                    onClick={async e => {
                      e.preventDefault()

                      setCurrentFilter(
                        currentFilter !== null &&
                          currentFilter?.name === category.name
                          ? null
                          : category
                      )
                      setFilterChanged(filterChanged + 1)

                      await multiPostHogEventPusher([
                        {
                          eventType: `${category.name} Video filter clicked`,
                        },
                      ])
                    }}
                    isActive={currentFilter?.name === category.name}
                    count={
                      category && currentFilter?.name === category.name
                        ? 'x'
                        : ''
                    }
                  />
                )
              )}
            </LozengeList>
          </div>
        )}
        {isMobile && (
          <div className={styles.mobileVideoSlider}>
            <DynamicSlider
              slidesPerView={1.15}
              slidesPerViewMed={1}
              slidesPerViewLrg={3}
              slidesPerViewHuge={4}
              overflowVisible={true}
              title={''}
              spaceBetween={0}
              hideArrowsMobile
              wrapperClass={styles.sliderWrapperClass}
            >
              {videoList &&
                videoList
                  .filter(slide => slide !== initialSlide)
                  .map((slide, key) => (
                    <div
                      key={`video-slide-${key}`}
                      className={styles.mobileSliderItem}
                      onClick={() => {
                        slideVideoSelector(slide)
                      }}
                    >
                      <VideoArticlePreview {...slide} theme={theme} />
                    </div>
                  ))}
            </DynamicSlider>
          </div>
        )}
        {!isMobile && (
          <div className={styles.videoHozList}>
            {/* Filter videoList to not include initialSlide */}

            {videoList &&
              videoList
                .filter(slide => slide !== initialSlide)
                .map((slide, key) => (
                  <div
                    key={`video-slide-${key}`}
                    onClick={() => {
                      slideVideoSelector(slide)
                    }}
                  >
                    <VideoArticlePreview {...slide} theme={theme} />
                  </div>
                ))}
          </div>
        )}
      </>
    )
  }, [
    currentActiveVideo,
    categoryList,
    videoList,
    setCurrentActiveVideo,
    isMobile,
  ])

  return (
    <Fixture
      theme={theme}
      verticalPadding={false}
      id={moduleId || 'videoModule'}
    >
      <div className={styles.base} data-testid={testID} ref={ref}>
        <Container gutters={gutterTypes.always}>
          {/* Video Module Head */}
          <div className={styles.head}>
            <Text
              Tag="h2"
              size={TextSizes.primaryTitleMed}
              className={styles.title}
            >
              <span dangerouslySetInnerHTML={{ __html: title }} />
            </Text>
          </div>
          {!filterableView ? renderInitialView() : renderFilterableView()}
        </Container>
      </div>
    </Fixture>
  )
}
